import { Footer } from "@/components/Footer"
import { Header } from "@/components/Header"
import { Subfooter } from "@/components/Subfooter"
import { Toaster } from "@/components/ui/toaster"
import * as React from "react"
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom"
import { AppApolloProvider } from "../authentication/AppApolloProvider"
import { AuthProvider } from "../authentication/AuthProvider"
import { ViewerProvider } from "../authentication/ViewerProvider"
import { RequireUserNotVerified } from "./layouts/RequireUserNotVerified"
import { RequireUserSignedIn } from "./layouts/RequireUserSignedIn"
import { RequireUserSignedOut } from "./layouts/RequireUserSignedOut"
import { RequireUserVerified } from "./layouts/RequireUserVerified"
import { NovusErrorBoundary } from "./novus-error-boundary"
import * as paths from "./novus-paths"
import { DashboardScreen } from "./screens/dashboard/DashboardScreen"
import AddDeductionScreen from "./screens/deductions/AddDeductionScreen"
import EditDeductionScreen from "./screens/deductions/EditDeductionScreen/EditDeductionScreen"
import AddFiveTwoNineAccountScreen from "./screens/five-two-nine-accounts/AddFiveTwoNineAccountScreen"
import EditFiveTwoNineAccountScreen from "./screens/five-two-nine-accounts/EditFiveTwoNineAccountScreen/EditFiveTwoNineAccountScreen"
import CreateAccountScreen from "./screens/onboarding/CreateAccountScreen"
import ForgotPasswordScreen from "./screens/onboarding/forgot-password/ForgotPasswordScreen"
import { OrganizationSigninScreen } from "./screens/onboarding/organization-signin/OrganizationSigninScreen"
import { OrganizationSignupScreen } from "./screens/onboarding/organization-signup/OrganizationSignupScreen"
import SignInScreen from "./screens/onboarding/SignInScreen"
import { OrgOnboardingScreen } from "./screens/organization-onboarding/OrgOnboardingScreen"
import EditProfileScreen from "./screens/profile/edit-profile/EditProfileScreen"
import UpdatePasswordScreen from "./screens/profile/update-password/UpdatePasswordScreen"

declare global {
  interface Window {
    novus: {
      googlePlayAppUrl: string
      appleAppUrl: string
      linkedInUrl: string
      maximumContributionAmountCents: string
    }
  }
}

const NovusLayout = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <Outlet />
      <Subfooter />
      <Footer />
    </div>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path={paths.rootPath.pattern}
      element={<NovusLayout />}
      errorElement={<NovusErrorBoundary />}
    >
      <Route element={<RequireUserSignedIn />}>
        <Route element={<RequireUserVerified />}>
          {/* Dashboard */}
          <Route index element={<DashboardScreen />} />

          {/* Deductions */}
          <Route
            path={paths.addDeductionPath.pattern}
            element={<AddDeductionScreen />}
          />
          <Route
            path={paths.editDeductionPath.pattern}
            element={<EditDeductionScreen />}
          />

          {/* 529 Accounts */}
          <Route
            path={paths.addBeneficiaryPath.pattern}
            element={<AddFiveTwoNineAccountScreen />}
          />
          <Route
            path={paths.editBeneficiaryPath.pattern}
            element={<EditFiveTwoNineAccountScreen />}
          />

          {/* Profile */}
          <Route
            path={paths.editProfilePath.pattern}
            element={<EditProfileScreen />}
          />
          <Route
            path={paths.updatePasswordPath.pattern}
            element={<UpdatePasswordScreen />}
          />
        </Route>

        <Route element={<RequireUserNotVerified />}>
          <Route
            path={paths.organizationOnboardingPath.pattern}
            element={<OrgOnboardingScreen />}
          />
        </Route>
      </Route>

      <Route element={<RequireUserSignedOut />}>
        {/* Organization Onboarding */}
        <Route
          path={paths.joinOrganizationSignupPath.pattern}
          element={<OrganizationSignupScreen />}
        />
        <Route
          path={paths.joinOrganizationSigninPath.pattern}
          element={<OrganizationSigninScreen />}
        />

        {/* Onboarding */}
        <Route path={paths.signInPath.pattern} element={<SignInScreen />} />
        <Route
          path={paths.forgotPasswordPath.pattern}
          element={<ForgotPasswordScreen />}
        />
        <Route
          path={paths.createAccountPath.pattern}
          element={<CreateAccountScreen />}
        />
      </Route>
    </Route>,
  ),
)

export const App = () => {
  return (
    <AuthProvider context="novus">
      <AppApolloProvider>
        <ViewerProvider>
          <Toaster />
          <RouterProvider router={router} />
        </ViewerProvider>
      </AppApolloProvider>
    </AuthProvider>
  )
}
